import * as THREE from 'three';

/**
 * CATIA-Tarzı GPU-Based Dinamik Kesit Sistemi
 *
 * THREE.js native clipping planes + Stencil Buffer Cap
 * - Native clipping: %100 GPU-based
 * - Kesit çizgileri: Plane-mesh intersection
 * - Cap Render: Stencil buffer tekniği (profesyonel CAD standardı)
 */
export class FikoDinamikKesitShader {

    // Store original clippingPlanes for restoration
    private static originalClippingPlanes: Map<THREE.Material, THREE.Plane[] | null> = new Map();

    // Section geometry groups
    private static sectionGroups: Map<THREE.Plane, THREE.Group> = new Map();

    // Cap render groups
    private static capGroups: Map<THREE.Plane, THREE.Group> = new Map();

    // Renderer reference
    private static renderer: THREE.WebGLRenderer | null = null;

    // Scene reference
    private static scene: THREE.Scene | null = null;

    // Camera reference
    private static camera: THREE.Camera | null = null;

    /**
     * Renderer, scene ve camera'yı ayarla
     */
    static setRenderer(renderer: THREE.WebGLRenderer, scene?: THREE.Scene, camera?: THREE.Camera): void {
        FikoDinamikKesitShader.renderer = renderer;
        if (scene) FikoDinamikKesitShader.scene = scene;
        if (camera) FikoDinamikKesitShader.camera = camera;
    }

    /**
     * Scene ve Camera referanslarını ayarla
     */
    static setSceneAndCamera(scene: THREE.Scene, camera: THREE.Camera): void {
        FikoDinamikKesitShader.scene = scene;
        FikoDinamikKesitShader.camera = camera;
    }

    /**
     * Material'e clipping planes ekle
     */
    static applyClipping(material: THREE.Material, planes: THREE.Plane[]): void {
        if (!FikoDinamikKesitShader.originalClippingPlanes.has(material)) {
            FikoDinamikKesitShader.originalClippingPlanes.set(
                material,
                material.clippingPlanes ? [...material.clippingPlanes] : null
            );
        }

        material.clippingPlanes = planes;
        material.clipIntersection = false;
        material.needsUpdate = true;
    }

    /**
     * Material'den clipping'i kaldır
     */
    static removeClipping(material: THREE.Material): void {
        const original = FikoDinamikKesitShader.originalClippingPlanes.get(material);

        if (original !== undefined) {
            material.clippingPlanes = original;
            FikoDinamikKesitShader.originalClippingPlanes.delete(material);
        } else {
            material.clippingPlanes = null;
        }

        material.needsUpdate = true;
    }

    /**
     * Kesit çizgileri ve cap (kapak) oluştur
     */
    static createSectionFill(
        plane: THREE.Plane,
        meshes: THREE.Mesh[],
        fillColor: number = 0xff6600,
        fillOpacity: number = 0.5,
        lineColor: number = 0xff0000,
        lineThickness: number = 2,
        lineVisible: boolean = true,
        showCap: boolean = false
    ): THREE.Group {
        const group = new THREE.Group();
        group.name = 'section_group';

        // ===== TÜM MESH'LERDEN KESİŞİM EDGE'LERİNİ TOPLA =====
        const allEdges: Array<{ start: THREE.Vector3, end: THREE.Vector3 }> = [];

        meshes.forEach(mesh => {
            if (!mesh.geometry) return;
            const edges = FikoDinamikKesitShader.computeIntersectionEdges(mesh, plane);
            allEdges.push(...edges);
        });

        // ===== KESİT ÇİZGİLERİ =====
        if (allEdges.length > 0) {
            const linePositions: number[] = [];
            allEdges.forEach(edge => {
                linePositions.push(
                    edge.start.x, edge.start.y, edge.start.z,
                    edge.end.x, edge.end.y, edge.end.z
                );
            });

            const lineGeometry = new THREE.BufferGeometry();
            lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));

            const lineMaterial = new THREE.LineBasicMaterial({
                color: lineColor,
                linewidth: lineThickness,
                depthTest: false,
                depthWrite: false,
                transparent: true,
                opacity: 1.0
            });

            const lineSegments = new THREE.LineSegments(lineGeometry, lineMaterial);
            lineSegments.renderOrder = 999;
            lineSegments.name = 'section_line';
            lineSegments.visible = lineVisible;
            lineSegments.userData = {
                isSectionLine: true,
                plane: plane,
                draggable: true
            };

            group.add(lineSegments);
        }

        // ===== CAP (KAPAK) - EDGE TRIANGULATION =====
        if (showCap && allEdges.length > 0) {
            const capGroup = FikoDinamikKesitShader.createCapFromEdgeTriangulation(
                plane,
                allEdges,
                fillColor,
                fillOpacity
            );
            if (capGroup) {
                capGroup.name = 'section_cap_group';
                group.add(capGroup);
                FikoDinamikKesitShader.capGroups.set(plane, capGroup);
            }
        }

        FikoDinamikKesitShader.sectionGroups.set(plane, group);
        return group;
    }

    // Reusable vectors for performance (zero allocation in hot path)
    private static readonly _v0 = new THREE.Vector3();
    private static readonly _v1 = new THREE.Vector3();
    private static readonly _v2 = new THREE.Vector3();
    private static readonly _intersect = new THREE.Vector3();

    /**
     * Plane-mesh kesişim edge'lerini hesapla
     * PERFORMANS: Pre-allocated vectors, inline calculations
     */
    private static computeIntersectionEdges(
        mesh: THREE.Mesh,
        plane: THREE.Plane
    ): Array<{ start: THREE.Vector3, end: THREE.Vector3 }> {
        const edges: Array<{ start: THREE.Vector3, end: THREE.Vector3 }> = [];
        const geometry = mesh.geometry;

        if (!geometry || !geometry.attributes.position) {
            return edges;
        }

        mesh.updateMatrixWorld(true);

        const positions = geometry.attributes.position;
        const indices = geometry.index;
        const worldMatrix = mesh.matrixWorld;
        const planeNormal = plane.normal;
        const planeConstant = plane.constant;

        // Pre-allocated vectors
        const v0 = FikoDinamikKesitShader._v0;
        const v1 = FikoDinamikKesitShader._v1;
        const v2 = FikoDinamikKesitShader._v2;

        const EPS = 0.0001;

        const triangleCount = indices ? indices.count / 3 : positions.count / 3;

        for (let i = 0; i < triangleCount; i++) {
            let i0: number, i1: number, i2: number;

            if (indices) {
                const base = i * 3;
                i0 = indices.getX(base);
                i1 = indices.getX(base + 1);
                i2 = indices.getX(base + 2);
            } else {
                i0 = i * 3;
                i1 = i0 + 1;
                i2 = i0 + 2;
            }

            // Inline vertex read + transform
            v0.set(positions.getX(i0), positions.getY(i0), positions.getZ(i0)).applyMatrix4(worldMatrix);
            v1.set(positions.getX(i1), positions.getY(i1), positions.getZ(i1)).applyMatrix4(worldMatrix);
            v2.set(positions.getX(i2), positions.getY(i2), positions.getZ(i2)).applyMatrix4(worldMatrix);

            // Inline distance calculation (avoid function call overhead)
            const d0 = planeNormal.x * v0.x + planeNormal.y * v0.y + planeNormal.z * v0.z + planeConstant;
            const d1 = planeNormal.x * v1.x + planeNormal.y * v1.y + planeNormal.z * v1.z + planeConstant;
            const d2 = planeNormal.x * v2.x + planeNormal.y * v2.y + planeNormal.z * v2.z + planeConstant;

            // Early exit: tüm vertices aynı tarafta
            if ((d0 > EPS && d1 > EPS && d2 > EPS) || (d0 < -EPS && d1 < -EPS && d2 < -EPS)) {
                continue;
            }

            // Kesişim noktalarını bul
            let intersectCount = 0;
            let startX = 0, startY = 0, startZ = 0;
            let endX = 0, endY = 0, endZ = 0;

            // Edge 0-1
            if ((d0 < -EPS && d1 > EPS) || (d0 > EPS && d1 < -EPS)) {
                const t = d0 / (d0 - d1);
                if (intersectCount === 0) {
                    startX = v0.x + (v1.x - v0.x) * t;
                    startY = v0.y + (v1.y - v0.y) * t;
                    startZ = v0.z + (v1.z - v0.z) * t;
                } else {
                    endX = v0.x + (v1.x - v0.x) * t;
                    endY = v0.y + (v1.y - v0.y) * t;
                    endZ = v0.z + (v1.z - v0.z) * t;
                }
                intersectCount++;
            }

            // Edge 1-2
            if ((d1 < -EPS && d2 > EPS) || (d1 > EPS && d2 < -EPS)) {
                const t = d1 / (d1 - d2);
                if (intersectCount === 0) {
                    startX = v1.x + (v2.x - v1.x) * t;
                    startY = v1.y + (v2.y - v1.y) * t;
                    startZ = v1.z + (v2.z - v1.z) * t;
                } else {
                    endX = v1.x + (v2.x - v1.x) * t;
                    endY = v1.y + (v2.y - v1.y) * t;
                    endZ = v1.z + (v2.z - v1.z) * t;
                }
                intersectCount++;
            }

            // Edge 2-0 (sadece 2 kesişim yoksa)
            if (intersectCount < 2 && ((d2 < -EPS && d0 > EPS) || (d2 > EPS && d0 < -EPS))) {
                const t = d2 / (d2 - d0);
                if (intersectCount === 0) {
                    startX = v2.x + (v0.x - v2.x) * t;
                    startY = v2.y + (v0.y - v2.y) * t;
                    startZ = v2.z + (v0.z - v2.z) * t;
                } else {
                    endX = v2.x + (v0.x - v2.x) * t;
                    endY = v2.y + (v0.y - v2.y) * t;
                    endZ = v2.z + (v0.z - v2.z) * t;
                }
                intersectCount++;
            }

            if (intersectCount >= 2) {
                edges.push({
                    start: new THREE.Vector3(startX, startY, startZ),
                    end: new THREE.Vector3(endX, endY, endZ)
                });
            }
        }

        return edges;
    }

    // Reusable vectors for cap rendering
    private static readonly _tangent = new THREE.Vector3();
    private static readonly _bitangent = new THREE.Vector3();
    private static readonly _planeOrigin = new THREE.Vector3();
    private static readonly _relative = new THREE.Vector3();
    private static readonly _p3d = new THREE.Vector3();

    /**
     * Edge'lerden kapalı contour'lar oluştur ve triangulate et
     * En büyük (dış sınır) contour'u atlar, sadece iç contour'ları render eder
     * PERFORMANS: Pre-allocated vectors, inline calculations, cached material
     */
    private static createCapFromEdgeTriangulation(
        plane: THREE.Plane,
        allEdges: Array<{ start: THREE.Vector3, end: THREE.Vector3 }>,
        fillColor: number,
        fillOpacity: number
    ): THREE.Group | null {
        if (allEdges.length === 0) return null;

        const capGroup = new THREE.Group();
        capGroup.name = 'cap_triangulation_group';

        // Edge'leri kapalı contour'lara dönüştür
        const contours = FikoDinamikKesitShader.buildClosedContours(allEdges);

        if (contours.length === 0) {
            return null;
        }

        // Plane'in local koordinat sistemini oluştur (reusable vectors)
        const planeNormal = plane.normal;
        const tangent = FikoDinamikKesitShader._tangent;
        const bitangent = FikoDinamikKesitShader._bitangent;
        const planeOrigin = FikoDinamikKesitShader._planeOrigin;

        // Orthogonal vektörler
        if (Math.abs(planeNormal.x) < 0.9) {
            tangent.set(1, 0, 0);
        } else {
            tangent.set(0, 1, 0);
        }
        bitangent.crossVectors(planeNormal, tangent).normalize();
        tangent.crossVectors(bitangent, planeNormal).normalize();
        planeOrigin.copy(planeNormal).multiplyScalar(-plane.constant);

        // Contour'ları ve alanlarını hesapla
        const contourData: { points2D: THREE.Vector2[], area: number }[] = [];

        for (let idx = 0; idx < contours.length; idx++) {
            const contour = contours[idx];
            if (contour.length < 3) continue;

            const points2D: THREE.Vector2[] = new Array(contour.length);
            let area = 0;

            // 2D projection + alan hesaplama (tek geçişte)
            for (let i = 0; i < contour.length; i++) {
                const p3d = contour[i];
                const rx = p3d.x - planeOrigin.x;
                const ry = p3d.y - planeOrigin.y;
                const rz = p3d.z - planeOrigin.z;

                const u = tangent.x * rx + tangent.y * ry + tangent.z * rz;
                const v = bitangent.x * rx + bitangent.y * ry + bitangent.z * rz;
                points2D[i] = new THREE.Vector2(u, v);
            }

            // Shoelace formula
            for (let i = 0; i < points2D.length; i++) {
                const j = (i + 1) % points2D.length;
                area += points2D[i].x * points2D[j].y - points2D[j].x * points2D[i].y;
            }

            contourData.push({ points2D, area: Math.abs(area) * 0.5 });
        }

        if (contourData.length === 0) return null;

        // Alan'a göre sırala ve en büyüğü atla
        contourData.sort((a, b) => b.area - a.area);

        const contoursToRender = contourData.length > 1
            ? contourData.slice(1)
            : contourData;

        // Shared material (tüm cap mesh'ler için tek material)
        const capMaterial = new THREE.MeshBasicMaterial({
            color: fillColor,
            transparent: true,
            opacity: fillOpacity,
            side: THREE.DoubleSide,
            depthTest: true,
            depthWrite: false,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1
        });

        // Her contour için mesh oluştur
        for (let renderIdx = 0; renderIdx < contoursToRender.length; renderIdx++) {
            const { points2D } = contoursToRender[renderIdx];

            try {
                const shape = new THREE.Shape(points2D);
                const shapeGeometry = new THREE.ShapeGeometry(shape);

                // 2D -> 3D transform (inline)
                const positions = shapeGeometry.attributes.position;
                const posArray = positions.array as Float32Array;

                for (let i = 0; i < positions.count; i++) {
                    const i3 = i * 3;
                    const u = posArray[i3];
                    const v = posArray[i3 + 1];

                    posArray[i3] = planeOrigin.x + tangent.x * u + bitangent.x * v;
                    posArray[i3 + 1] = planeOrigin.y + tangent.y * u + bitangent.y * v;
                    posArray[i3 + 2] = planeOrigin.z + tangent.z * u + bitangent.z * v;
                }
                positions.needsUpdate = true;

                const capMesh = new THREE.Mesh(shapeGeometry, capMaterial);
                capMesh.renderOrder = 997;
                capMesh.name = `section_cap_contour_${renderIdx}`;
                capMesh.userData = { isSectionCap: true, contourIndex: renderIdx };

                capGroup.add(capMesh);
            } catch (e) {
                // Triangulation hatası - skip
            }
        }

        return capGroup.children.length > 0 ? capGroup : null;
    }

    /**
     * Edge segment'lerinden kapalı contour'lar oluştur
     * PERFORMANS: Spatial hash ile O(n²) -> O(n) edge matching
     */
    private static buildClosedContours(
        edges: Array<{ start: THREE.Vector3, end: THREE.Vector3 }>
    ): THREE.Vector3[][] {
        if (edges.length === 0) return [];

        const TOLERANCE = 0.001;
        const HASH_SCALE = 1000; // 0.001 hassasiyet için
        const contours: THREE.Vector3[][] = [];

        // Spatial hash for fast vertex lookup
        const vertexHash = new Map<string, number[]>();

        const hashKey = (v: THREE.Vector3): string => {
            const x = Math.round(v.x * HASH_SCALE);
            const y = Math.round(v.y * HASH_SCALE);
            const z = Math.round(v.z * HASH_SCALE);
            return `${x},${y},${z}`;
        };

        // Edge data structure
        const edgeData: { start: THREE.Vector3, end: THREE.Vector3, used: boolean }[] = new Array(edges.length);

        // Hash'e ekle
        for (let i = 0; i < edges.length; i++) {
            const e = edges[i];
            edgeData[i] = { start: e.start, end: e.end, used: false };

            // Start vertex hash
            const startKey = hashKey(e.start);
            if (!vertexHash.has(startKey)) vertexHash.set(startKey, []);
            vertexHash.get(startKey)!.push(i * 2); // Even = start

            // End vertex hash
            const endKey = hashKey(e.end);
            if (!vertexHash.has(endKey)) vertexHash.set(endKey, []);
            vertexHash.get(endKey)!.push(i * 2 + 1); // Odd = end
        }

        // Vertex match (inline distance check)
        const TOL_SQ = TOLERANCE * TOLERANCE;
        const verticesMatch = (v1: THREE.Vector3, v2: THREE.Vector3): boolean => {
            const dx = v1.x - v2.x;
            const dy = v1.y - v2.y;
            const dz = v1.z - v2.z;
            return dx * dx + dy * dy + dz * dz < TOL_SQ;
        };

        // Find next edge using spatial hash
        const findNextEdge = (currentEnd: THREE.Vector3, excludeIdx: number): { idx: number, reverse: boolean } | null => {
            const key = hashKey(currentEnd);

            // Check exact hash bucket and neighbors
            const neighbors = [key];
            // Optional: add neighbor buckets for tolerance

            for (const k of neighbors) {
                const candidates = vertexHash.get(k);
                if (!candidates) continue;

                for (const candidateId of candidates) {
                    const edgeIdx = Math.floor(candidateId / 2);
                    if (edgeIdx === excludeIdx || edgeData[edgeIdx].used) continue;

                    const edge = edgeData[edgeIdx];
                    const isStart = candidateId % 2 === 0;

                    if (isStart && verticesMatch(currentEnd, edge.start)) {
                        return { idx: edgeIdx, reverse: false };
                    }
                    if (!isStart && verticesMatch(currentEnd, edge.end)) {
                        return { idx: edgeIdx, reverse: true };
                    }
                }
            }

            // Fallback: linear search for tolerance matching
            for (let i = 0; i < edgeData.length; i++) {
                if (i === excludeIdx || edgeData[i].used) continue;
                const edge = edgeData[i];
                if (verticesMatch(currentEnd, edge.start)) {
                    return { idx: i, reverse: false };
                }
                if (verticesMatch(currentEnd, edge.end)) {
                    return { idx: i, reverse: true };
                }
            }

            return null;
        };

        // Build contours
        let startIdx = 0;
        while (startIdx < edgeData.length) {
            // Find unused edge
            while (startIdx < edgeData.length && edgeData[startIdx].used) {
                startIdx++;
            }
            if (startIdx >= edgeData.length) break;

            const contour: THREE.Vector3[] = [];
            let currentIdx = startIdx;
            const firstEdge = edgeData[startIdx];

            contour.push(firstEdge.start.clone());
            contour.push(firstEdge.end.clone());
            firstEdge.used = true;

            let currentEnd = firstEdge.end;
            const contourStart = firstEdge.start;

            let maxIterations = edges.length;
            while (maxIterations-- > 0) {
                // Check if closed
                if (contour.length >= 3 && verticesMatch(currentEnd, contourStart)) {
                    contours.push(contour);
                    break;
                }

                const next = findNextEdge(currentEnd, currentIdx);
                if (!next) {
                    if (contour.length >= 3) {
                        contours.push(contour);
                    }
                    break;
                }

                const nextEdge = edgeData[next.idx];
                nextEdge.used = true;

                if (next.reverse) {
                    contour.push(nextEdge.start.clone());
                    currentEnd = nextEdge.start;
                } else {
                    contour.push(nextEdge.end.clone());
                    currentEnd = nextEdge.end;
                }
                currentIdx = next.idx;
            }
        }

        return contours;
    }

    /**
     * Section çizgilerini ve cap'i güncelle
     */
    static updateSectionFill(
        plane: THREE.Plane,
        meshes: THREE.Mesh[],
        group: THREE.Group,
        lineColor: number = 0xff0000,
        lineThickness: number = 2,
        lineVisible: boolean = true,
        showCap: boolean = false,
        fillColor: number = 0xff6600,
        fillOpacity: number = 0.5
    ): void {
        // Eski çizgileri ve cap group'larını temizle
        const oldObjects: THREE.Object3D[] = [];
        group.traverse(child => {
            if (child.name === 'section_line' ||
                child.name === 'section_cap_group' ||
                child.name === 'stencil_cap_group' ||
                child.name === 'cap_contour_group' ||
                child.name === 'cap_backface_group' ||
                child.name === 'cap_triangulation_group' ||
                child.name === 'section_cap_fan' ||
                child.name === 'section_cap_plane' ||
                child.name.startsWith('section_cap')) {
                oldObjects.push(child);
            }
        });

        oldObjects.forEach(obj => {
            FikoDinamikKesitShader.disposeObject(obj);
            group.remove(obj);
        });

        // Eski cap group'u temizle
        FikoDinamikKesitShader.capGroups.delete(plane);

        // Yeni edge'leri hesapla
        const allEdges: Array<{ start: THREE.Vector3, end: THREE.Vector3 }> = [];

        meshes.forEach(mesh => {
            if (!mesh.geometry) return;
            const edges = FikoDinamikKesitShader.computeIntersectionEdges(mesh, plane);
            allEdges.push(...edges);
        });

        // ===== KESİT ÇİZGİLERİ =====
        if (allEdges.length > 0) {
            const linePositions: number[] = [];
            allEdges.forEach(edge => {
                linePositions.push(
                    edge.start.x, edge.start.y, edge.start.z,
                    edge.end.x, edge.end.y, edge.end.z
                );
            });

            const lineGeometry = new THREE.BufferGeometry();
            lineGeometry.setAttribute('position', new THREE.Float32BufferAttribute(linePositions, 3));

            const lineMaterial = new THREE.LineBasicMaterial({
                color: lineColor,
                linewidth: lineThickness,
                depthTest: false,
                depthWrite: false,
                transparent: true,
                opacity: 1.0
            });

            const lineSegments = new THREE.LineSegments(lineGeometry, lineMaterial);
            lineSegments.renderOrder = 999;
            lineSegments.name = 'section_line';
            lineSegments.visible = lineVisible;
            lineSegments.userData = {
                isSectionLine: true,
                plane: plane,
                draggable: true
            };

            group.add(lineSegments);
        }

        // ===== CAP (KAPAK) =====
        if (showCap && allEdges.length > 0) {
            const capGroup = FikoDinamikKesitShader.createCapFromEdgeTriangulation(
                plane,
                allEdges,
                fillColor,
                fillOpacity
            );
            if (capGroup) {
                capGroup.name = 'section_cap_group';
                group.add(capGroup);
                FikoDinamikKesitShader.capGroups.set(plane, capGroup);
            }
        }
    }

    /**
     * Object ve child'larını dispose et
     */
    private static disposeObject(obj: THREE.Object3D): void {
        obj.traverse(child => {
            const mesh = child as THREE.Mesh;
            if (mesh.geometry) mesh.geometry.dispose();
            if (mesh.material) {
                const mat = mesh.material;
                if (Array.isArray(mat)) {
                    mat.forEach(m => m.dispose());
                } else {
                    (mat as THREE.Material).dispose();
                }
            }
        });
    }

    /**
     * Section group kaldır
     */
    static removeSectionFill(plane: THREE.Plane): void {
        const group = FikoDinamikKesitShader.sectionGroups.get(plane);
        if (group) {
            FikoDinamikKesitShader.disposeObject(group);
            group.parent?.remove(group);
            FikoDinamikKesitShader.sectionGroups.delete(plane);
        }

        FikoDinamikKesitShader.capGroups.delete(plane);
    }

    /**
     * Cap görünürlüğünü toggle et
     */
    static setCapVisibility(plane: THREE.Plane, visible: boolean): void {
        const capGroup = FikoDinamikKesitShader.capGroups.get(plane);
        if (capGroup) {
            capGroup.visible = visible;
        }
    }

    /**
     * Tüm cap'lerin görünürlüğünü toggle et
     */
    static setAllCapsVisibility(visible: boolean): void {
        FikoDinamikKesitShader.capGroups.forEach(capGroup => {
            capGroup.visible = visible;
        });
    }

    /**
     * Cap rengini güncelle
     */
    static updateCapColor(plane: THREE.Plane, color: number, opacity?: number): void {
        const capGroup = FikoDinamikKesitShader.capGroups.get(plane);
        if (capGroup) {
            capGroup.traverse(child => {
                const mesh = child as THREE.Mesh;
                if (mesh.material) {
                    const mat = mesh.material as THREE.MeshBasicMaterial;
                    if (mat.color) {
                        mat.color.setHex(color);
                        if (opacity !== undefined) {
                            mat.opacity = opacity;
                        }
                        mat.needsUpdate = true;
                    }
                }
            });
        }
    }

    /**
     * Cleanup
     */
    static dispose(): void {
        FikoDinamikKesitShader.originalClippingPlanes.clear();
        FikoDinamikKesitShader.sectionGroups.forEach((_, plane) => {
            FikoDinamikKesitShader.removeSectionFill(plane);
        });
        FikoDinamikKesitShader.sectionGroups.clear();
        FikoDinamikKesitShader.capGroups.clear();
        FikoDinamikKesitShader.renderer = null;
        FikoDinamikKesitShader.scene = null;
        FikoDinamikKesitShader.camera = null;
    }
}
